import type { LogicalDeviceScreenSharePageProps } from '@ingenico/type-mfe-contracts/domains';
import { SectionPageHeader, useSetHeaderMobileInformation } from '@ingenico/react-section-utils';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { ArrowBack } from '@mui/icons-material';
import AppsIcon from '@mui/icons-material/Apps';
import HomeIcon from '@mui/icons-material/Home';
import { Button, IconButton, useMediaQuery } from '@mui/material';
import { Box } from '@mui/system';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalScreenShare } from '../../components/modal-screen-share/ModalScreenShare.ctrl';
import { ScreenShareSessionStatus } from '../../domain/models/screenShare.model';
import styles from './logicalDeviceScreenSharePage.module.scss';
import { useLogicalDeviceScreenSharePageVm } from './useLogicalDeviceScreenSharePage.vm';

export function LogicalDeviceScreenSharePage({
  logicalDeviceId,
  deviceModel,
  deviceName,
  serialNumber,
}: LogicalDeviceScreenSharePageProps): ReactElement {
  const {
    initStatus,
    sessionStatus,
    handleStopSession,
    handleRetry,
    handleHomeButton,
    handleAppsButton,
    handleBackButton,
    screenShareContainer,
    handleCloseModal,
    isModalOpened,
    data: { deviceModel: resolvedDeviceModel, deviceName: resolvedDeviceName, serialNumber: resolvedSerialNumber },
  } = useLogicalDeviceScreenSharePageVm({ logicalDeviceId, deviceModel, deviceName, serialNumber });

  const isMobile = useMediaQuery('(max-width: 766px)');
  const { t } = useTranslation(ALL_LNGS_NAMESPACES.LOGICAL_DEVICE);

  const setHeaderMobileInformation = useSetHeaderMobileInformation();

  useEffect(() => {
    setHeaderMobileInformation({
      title: resolvedDeviceName,
      subtitle: `${resolvedDeviceModel} • ${resolvedSerialNumber}`,
      kebabOptions: [
        {
          label: t('screenSharePage.actions.stopSession'),
          onClick: handleStopSession,
          key: 'stop-session',
        },
        {
          label: t('screenSharePage.actions.retry'),
          onClick: handleRetry,
          key: 'retry-session',
        },
      ],
    });
  }, [
    resolvedDeviceModel,
    resolvedDeviceName,
    handleRetry,
    handleStopSession,
    resolvedSerialNumber,
    sessionStatus,
    setHeaderMobileInformation,
    t,
  ]);

  return (
    <>
      {!isMobile && (
        <SectionPageHeader
          isWhiteBackground
          rightContent={
            <Box>
              <Button style={{ marginRight: '8px' }} variant={'contained'} onClick={handleStopSession}>
                {t('screenSharePage.actions.stopSession')}
              </Button>
              <Button variant={'outlined'} onClick={handleRetry}>
                {t('screenSharePage.actions.retry')}
              </Button>
            </Box>
          }
          subtitle={`${resolvedDeviceModel} • ${resolvedSerialNumber}`}
          title={resolvedDeviceName}
        />
      )}
      <div className={styles.container}>
        <Box className={styles.containerScreenShare} ref={screenShareContainer}></Box>
        <div className={styles.containerControl}>
          <IconButton
            color={'primary'}
            disabled={sessionStatus === ScreenShareSessionStatus.DISCONNECTED}
            onClick={handleBackButton}
          >
            <ArrowBack></ArrowBack>
          </IconButton>
          <IconButton disabled={sessionStatus === ScreenShareSessionStatus.DISCONNECTED} onClick={handleHomeButton}>
            <HomeIcon></HomeIcon>
          </IconButton>
          <IconButton disabled={sessionStatus === ScreenShareSessionStatus.DISCONNECTED} onClick={handleAppsButton}>
            <AppsIcon></AppsIcon>
          </IconButton>
        </div>
      </div>
      {isModalOpened && (
        <ModalScreenShare
          isConnecting={
            ((initStatus?.progressInPercent ?? 0) > 0 &&
              ![
                ScreenShareSessionStatus.CONNECTED,
                ScreenShareSessionStatus.RefusedByCustomer,
                ScreenShareSessionStatus.ERROR,
              ].includes(sessionStatus)) ||
            sessionStatus === ScreenShareSessionStatus.RECONNECTING
          }
          isFailure={sessionStatus === ScreenShareSessionStatus.ERROR}
          isNotAllowed={false}
          isSuccess={sessionStatus === ScreenShareSessionStatus.CONNECTED}
          onClose={handleCloseModal}
          progressInPercent={initStatus?.progressInPercent ?? 0}
          isRefusedByUser={sessionStatus === ScreenShareSessionStatus.RefusedByCustomer}
          triggerSignatureDevicePermissionCall={handleRetry}
        />
      )}
    </>
  );
}
